'use client';

import { Badge } from '@wilkins-software/universal-components';
import { Button } from '@wilkins-software/universal-components';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@wilkins-software/universal-components';
import { ContactCtaLink } from './contact/contact-cta-link.component';
import {
  Check,
  Heart,
  Users,
  Home,
  ArrowRight,
  ToggleRight,
  ToggleLeft,
} from 'react-feather';
import { PrimaryNavigationClient } from '../components/primaryNavigation/PrimaryNavigationClient';
import { Footer } from '../components/footer';
import { SOCIALS } from '../constants';
import { useState } from 'react';

type BillingPeriod = 'monthly' | 'annual';

const PRICING = {
  starter: { monthly: 49, annual: 529 },
  growth: { monthly: 149, annual: 1609 },
  pro: { monthly: 299, annual: 3229 },
} as const;

export default function AnimalSoftwarePricingClient() {
  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');

  const toggleBillingPeriod = () => {
    setBillingPeriod((prev) => (prev === 'monthly' ? 'annual' : 'monthly'));
  };

  const getPrice = (plan: keyof typeof PRICING) => {
    return PRICING[plan][billingPeriod];
  };

  const getSavings = (plan: keyof typeof PRICING) => {
    const monthlyCost = PRICING[plan].monthly * 12;
    const annualCost = PRICING[plan].annual;
    return monthlyCost - annualCost;
  };

  return (
    <div className="min-h-screen bg-background">
      <PrimaryNavigationClient isHeroImagePage={false} />
      <main>
        {/* Hero Section */}
        <section className="py-20 pt-32">
          <div className="homepage-wrapper text-center">
            <Badge
              variant="secondary"
              className="mb-6 bg-green-light text-primary border-primary/20"
            >
              <Heart className="w-3 h-3 mr-1" />
              Helping Shelters Help Animals
            </Badge>
            <h1 className="text-4xl md:text-6xl font-bold text-balance mb-6">
              Shelter Sense
              <span className="text-primary block">
                Animal Welfare Management
              </span>
            </h1>
            <p className="text-xl text-muted-foreground text-balance mb-8 max-w-2xl mx-auto">
              Our ready-to-use platform helps you track animal wellbeing, manage
              volunteers, and streamline shelter operations. Available at the
              prices below, or we can customize it to fit your unique needs.
            </p>

            <div className="flex items-center justify-center space-x-4 mb-8">
              <span
                className={`text-sm font-medium transition-colors ${billingPeriod === 'monthly' ? 'text-primary' : 'text-muted-foreground'}`}
              >
                Monthly
              </span>
              <button
                onClick={toggleBillingPeriod}
                className="relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded"
                aria-label={`Switch to ${billingPeriod === 'monthly' ? 'annual' : 'monthly'} billing`}
              >
                {billingPeriod === 'monthly' ? (
                  <ToggleLeft className="w-8 h-8 text-muted-foreground hover:text-primary transition-colors" />
                ) : (
                  <ToggleRight className="w-8 h-8 text-primary transition-colors" />
                )}
              </button>
              <span
                className={`text-sm font-medium transition-colors ${billingPeriod === 'annual' ? 'text-primary' : 'text-muted-foreground'}`}
              >
                Annual
              </span>
              <Badge variant="secondary" className="bg-primary/10 text-primary">
                Save 10%
              </Badge>
            </div>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-16">
          <div className="homepage-wrapper">
            <div className="grid md:grid-cols-3 gap-8 mb-12">
              {/* Starter Plan */}
              <Card className="relative border-border/50 hover:border-primary/50 transition-colors flex flex-col h-full">
                <CardHeader className="pb-8">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">Starter</CardTitle>
                    <Users className="w-5 h-5 text-muted-foreground" />
                  </div>
                  <div className="flex items-baseline space-x-1">
                    <span className="text-4xl font-bold">
                      ${getPrice('starter')}
                    </span>
                    <span className="text-muted-foreground">
                      /{billingPeriod === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                  {billingPeriod === 'annual' && (
                    <p className="text-sm text-green-600 font-medium">
                      Save ${getSavings('starter')} per year
                    </p>
                  )}
                  <CardDescription className="text-base">
                    Ideal for getting started: simple, affordable, all the
                    essentials
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 flex-1">
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Track walks, playgroups, and enrichment activities
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Basic medical and behavioral tracking
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">Unlimited staff access</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Email support (48-hour response time)
                    </span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full bg-transparent"
                    variant="outline"
                    asChild
                  >
                    <a
                      href="https://sheltersense.ca/free-trial"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Start with Starter
                    </a>
                  </Button>
                </CardFooter>
              </Card>

              {/* Growth Plan - Featured */}
              <Card className="relative border-primary shadow-2xl scale-[1.08] bg-gradient-to-b from-background to-primary/5 flex flex-col h-full">
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <Badge className="bg-primary text-primary-foreground px-4 py-1 text-sm font-semibold">
                    Most Popular Choice
                  </Badge>
                </div>
                <CardHeader className="pb-8 pt-10">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl text-primary">
                      Growth
                    </CardTitle>
                    <Home className="w-5 h-5 text-primary" />
                  </div>
                  <div className="flex items-baseline space-x-1">
                    <span className="text-4xl font-bold text-primary">
                      ${getPrice('growth')}
                    </span>
                    <span className="text-muted-foreground">
                      /{billingPeriod === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                  {billingPeriod === 'annual' && (
                    <p className="text-sm text-green-600 font-medium">
                      Save ${getSavings('growth')} per year
                    </p>
                  )}
                  <CardDescription className="text-base">
                    For shelters ready to enhance animal welfare with
                    comprehensive tracking
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 flex-1">
                  <div className="text-sm text-muted-foreground mb-3">
                    Everything in Starter, plus:
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Advanced enrichment analytics and welfare insights
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Dedicated onboarding and staff training
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Quarterly welfare outcome reviews
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Priority support (same-day response)
                    </span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg"
                    asChild
                  >
                    <a
                      href="https://sheltersense.ca/free-trial"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Choose Growth
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </a>
                  </Button>
                </CardFooter>
              </Card>

              {/* Pro Plan */}
              <Card className="relative border-border/50 hover:border-primary/50 transition-colors flex flex-col h-full">
                <CardHeader className="pb-8">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-xl">Pro</CardTitle>
                    <div className="w-5 h-5 bg-primary rounded-sm flex items-center justify-center">
                      <div className="w-2 h-2 bg-primary-foreground rounded-sm"></div>
                    </div>
                  </div>
                  <div className="flex items-baseline space-x-1">
                    <span className="text-4xl font-bold">
                      ${getPrice('pro')}
                    </span>
                    <span className="text-muted-foreground">
                      /{billingPeriod === 'monthly' ? 'month' : 'year'}
                    </span>
                  </div>
                  {billingPeriod === 'annual' && (
                    <p className="text-sm text-green-600 font-medium">
                      Save ${getSavings('pro')} per year
                    </p>
                  )}
                  <CardDescription className="text-base">
                    For large shelters with advanced enrichment reporting and
                    integration needs
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 flex-1">
                  <div className="text-sm text-muted-foreground mb-3">
                    Everything in Growth, plus:
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Custom enrichment protocols and tracking templates
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Integration with veterinary and adoption systems
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Unlimited custom reports and data exports
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm">
                      Early access to new enrichment tracking features
                    </span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full bg-primary hover:bg-primary/90"
                    asChild
                  >
                    <a
                      href="https://sheltersense.ca/free-trial"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Choose Pro
                    </a>
                  </Button>
                </CardFooter>
              </Card>
            </div>

            <div className="max-w-4xl mx-auto">
              <Card className="bg-muted/20 border-muted-foreground/20">
                <CardContent className="p-6">
                  <div className="text-center">
                    <h3 className="text-lg font-semibold mb-2 text-muted-foreground">
                      Need Something Custom?
                    </h3>
                    <p className="text-sm text-muted-foreground mb-4">
                      We can customize Shelter Sense to fit your unique
                      requirements, or build a completely custom solution from
                      scratch
                    </p>
                    <div className="flex flex-wrap justify-center gap-6 text-sm mb-6">
                      <div className="flex items-center space-x-2">
                        <Check className="w-4 h-4 text-primary" />
                        <span className="text-muted-foreground">
                          Dedicated account manager
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Check className="w-4 h-4 text-primary" />
                        <span className="text-muted-foreground">
                          Advanced security (SSO, audit logs)
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Check className="w-4 h-4 text-primary" />
                        <span className="text-muted-foreground">
                          Custom feature development
                        </span>
                      </div>
                    </div>
                    <Button variant="outline" size="sm" asChild>
                      <ContactCtaLink topicId="shelter-sense-custom-development">
                        Talk to Us About Custom Development
                      </ContactCtaLink>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="py-16 bg-muted/30">
          <div className="homepage-wrapper text-center">
            <h2 className="text-3xl font-bold mb-6">
              Why Choose Shelter Sense?
            </h2>
            <p className="text-muted-foreground mb-8">
              Built specifically for animal welfare organizations, Shelter Sense
              scales with your needs
            </p>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="text-left">
                <h3 className="text-lg font-semibold mb-4 text-primary">
                  For Small Shelters
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">
                      Track animal enrichment: walks, playtime, and clinic
                      visits
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">
                      Set and track basic care goals for each animal
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">
                      Simple permission levels for staff and volunteers
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">
                      Volunteer sign-in/out with activity tracking
                    </span>
                  </div>
                </div>
              </div>
              <div className="text-left">
                <h3 className="text-lg font-semibold mb-4 text-primary">
                  For Large Organizations
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">
                      Comprehensive enrichment analytics and behavioral insights
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">
                      Advanced goal setting with progress tracking across
                      departments
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">
                      Multi-level permission system with role-based access
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span className="text-sm text-muted-foreground">
                      Full volunteer organization with scheduling and impact
                      reporting
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="py-16">
          <div className="homepage-wrapper">
            <h2 className="text-2xl font-bold text-center mb-8">
              Frequently Asked Questions
            </h2>
            <div className="space-y-6">
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">
                    Do you offer nonprofit discounts?
                  </h3>
                  <p className="text-muted-foreground">
                    All our pricing already includes a nonprofit discount. We
                    believe in making shelter management affordable for
                    organizations focused on saving lives.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">
                    Can I switch plans anytime?
                  </h3>
                  <p className="text-muted-foreground">
                    Yes! You can upgrade or downgrade your plan at any time.
                    Changes take effect immediately, and we'll prorate any
                    billing differences.
                  </p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-6">
                  <h3 className="font-semibold mb-2">
                    What's included in the onboarding?
                  </h3>
                  <p className="text-muted-foreground">
                    Growth and Pro plans include a dedicated training session to
                    help your team get started, plus ongoing support to ensure
                    you're maximizing the platform's potential.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      </main>

      <Footer
        socialLinks={SOCIALS}
        copyrightText="© 2025 Wilkins Software · Shelter Sense. Animal welfare operations."
      />
    </div>
  );
}
